import { useMemo } from 'react'

import cn from 'classnames'
import { Loader2 } from 'lucide-react'

import { useWalletModal as useTronWalletModal } from '@tronweb3/tronwallet-adapter-react-ui'

import { ChainId } from '@src/configs/chainInfo'
import { StepNames } from '@src/enums'
import { useSwapButtonState } from '@src/hooks/useSwapButtonState'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useAuthContext } from '@src/providers/AuthProvider'
import { analyticsService } from '@src/services/analytics'
import { IExchangeData } from '@src/types/exchange'
import { FeatherQuoteError } from '@src/types/feather'
import { IQuoteResponse } from '@src/types/order'
import { isFeatureEnabled } from '@src/utils'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { useApplicationStore } from '@src/zustand_stores/application.store'

interface IErrors {
    isInsufficientBalance?: boolean
    isQuoteInvalid?: boolean
    isBuyOutputLoading?: boolean
    isDestinationSolana?: boolean
    isOriginSolana?: boolean
    isTransactionCapped?: boolean
    isLiquidityUnavailable?: boolean
    hasInsufficientGas?: boolean
}

interface Props extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    errorsJSON?: IErrors
    sellData: IExchangeData
    buyData?: IExchangeData
    sellAmountString?: string
    isCustomRecipientWallet?: boolean
    destinationAddress?: string
    liquiditySource?: string
    isOrderRejected?: boolean
    isQuoteExpired?: boolean
    currentStep?: StepNames
    hasInsufficientGas?: boolean
    quoteResponse?: IQuoteResponse
    gasFeeSimulationInProgress?: boolean
    waitingForConnectedWalletInteraction?: boolean
    isBuyChainWalletConnected?: boolean
    isSellChainWalletConnected?: boolean
    isQuoteLoading?: boolean
    isQuoteFetching?: boolean
    buttonText?: string
    liquidityAvailable?: boolean
    isCheckingLiquidity?: boolean
    liquidityError?: string | null
    featherQuoteError?: FeatherQuoteError | null
    onMetaMaskPopup?: (step: StepNames) => void
}

export const SwapButton = ({
    errorsJSON,
    sellData,
    buyData,
    className,
    buttonText = 'Swap',
    isCustomRecipientWallet = false,
    destinationAddress = '',
    liquiditySource,
    isOrderRejected = false,
    isQuoteExpired = false,
    currentStep = StepNames.UNSTARTED,
    hasInsufficientGas = false,
    quoteResponse = undefined,
    onMetaMaskPopup,
    gasFeeSimulationInProgress,
    sellAmountString = undefined,
    featherQuoteError = null,
    liquidityAvailable = true,
    isCheckingLiquidity = false,
    liquidityError = null,
    disabled,
    ...props
}: Props) => {
    const { waitingForConnectedWalletInteraction } = useApplicationStore()
    const { isSolanaConnected, isTronConnected, isEVMConnected, EVMChainId } = useWalletConnections()
    const { connectHandler: connectEVMOrSolana } = useAuthContext()

    // For Tron wallet connection
    const { setVisible: openTronWalletModal } = useTronWalletModal()

    // Use the analytics service

    // Connect wallet function that handles different wallet types
    const connectWallet = (chainId: ChainId) => {
        try {
            // Track the main wallet connection attempt
            const walletType = chainId === ChainId.Tron ? 'Tron' : chainId === ChainId.Solana ? 'Solana' : 'EVM'

            // Track the main wallet connection attempt event
            analyticsService.trackEvent('wallet_main_attempt_connect', {
                wallet_type: walletType,
                button_location: 'swap_screen', // This is the main swap button
                timestamp: new Date().toISOString(),
            })

            // Note: Specific wallet connection attempts (MetaMask, Phantom, etc.)
            // will be tracked in their respective connection handlers

            if (chainId === ChainId.Tron) {
                // For Tron, open the wallet modal
                openTronWalletModal(true)
            } else {
                // For EVM and Solana, use the AuthProvider
                connectEVMOrSolana()
            }
        } catch (error) {
            // Track the wallet connection failure
            const walletName =
                chainId === ChainId.Tron ? 'TronLink' : chainId === ChainId.Solana ? 'Phantom' : 'MetaMask'
            const chainType = chainId === ChainId.Tron ? 'TRX' : chainId === ChainId.Solana ? 'SVM' : 'EVM'

            analyticsService.trackWalletConnectionFailed(error, walletName, chainType)

            // Re-throw the error to maintain the original behavior
            throw error
        }
    }

    const isBuyChainWalletConnected = useMemo(() => {
        if (!buyData) {
            return true // Skip check if buyData isn't available
        }

        switch (buyData.chainId) {
            case ChainId.Solana:
                return isSolanaConnected
            case ChainId.Tron:
                return isTronConnected
            default:
                return isEVMConnected
        }
    }, [buyData, isSolanaConnected, isTronConnected, isEVMConnected, EVMChainId])

    const isSellChainWalletConnected = useMemo(() => {
        switch (sellData.chainId) {
            case ChainId.Solana:
                return isSolanaConnected
            case ChainId.Tron:
                return isTronConnected
            default:
                return isEVMConnected
        }
    }, [sellData, isSolanaConnected, isTronConnected, isEVMConnected])

    // Destructure errors from errorsJSON
    const {
        isInsufficientBalance = false,
        isQuoteInvalid = false,
        isBuyOutputLoading = false,
        isDestinationSolana = false,
        isOriginSolana = false,
        isTransactionCapped = false,
        // Use liquidityAvailable prop directly instead of from errorsJSON
    } = errorsJSON || {}

    // Use the shared hook for button state
    const {
        buttonDisabled: calculatedButtonDisabled,
        buttonText: buttonTextValue,
        isPendingState,
        needsWalletConnection,
        getWalletConnectionText,
        isAddRecipientCase,
    } = useSwapButtonState({
        sellData,
        buyData,
        sellAmountString,
        isInsufficientBalance,
        hasInsufficientGas,
        isBuyOutputLoading,
        isQuoteInvalid,
        isCustomRecipientWallet,
        destinationAddress,
        liquiditySource,
        isOrderRejected,
        isQuoteExpired,
        currentStep,
        quoteResponse,
        gasFeeSimulationInProgress,
        waitingForConnectedWalletInteraction,
        isBuyChainWalletConnected,
        isSellChainWalletConnected,
        isQuoteLoading: props.isQuoteLoading,
        isQuoteFetching: props.isQuoteFetching,
        isLiquidityUnavailable: !liquidityAvailable, // Derive from liquidityAvailable prop
        isCheckingLiquidity,
        liquidityError,
    })

    // Final disabled state, combining calculated state with prop override
    // Note: isLiquidityUnavailable is already included in buttonDisabled from the hook
    const finalButtonDisabled =
        disabled !== undefined ? disabled : calculatedButtonDisabled || isPendingState || !liquidityAvailable

    const buttonClass = cn(
        // Use the custom-wallet-button class when button is for wallet connection
        buttonTextValue.includes('Connect')
            ? 'custom-wallet-button'
            : 'flex select-none items-center justify-center space-x-[10px] rounded-2xl px-[18px] py-[14px] font-medium leading-7 transition-all',
        // Only apply these background styles when not in wallet connection mode
        !buttonTextValue.includes('Connect') && {
            // Show the disabled style for error states, Add Recipient case, and when liquidity is unavailable
            'bg-mach-component-background':
                (finalButtonDisabled && !buttonTextValue.includes('Connect')) || isAddRecipientCase,
            // Show blue button for all enabled buttons (including "Confirm Swap")
            'bg-mach-button-color hover:brightness-110':
                (!finalButtonDisabled || buttonTextValue.includes('Connect')) && !isAddRecipientCase,
        },
        className
    )

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        // Handle wallet connection if needed
        if (needsWalletConnection) {
            const walletType = getWalletConnectionText

            // If we need a custom recipient address, don't try to connect a wallet
            // Instead let the parent component handle showing the address input
            if (walletType === 'Add Recipient') {
                // Don't prevent the event in this case - we want the parent handler to run
                return
            }

            // Otherwise we're connecting a wallet, so prevent the event from propagating
            event.stopPropagation()
            event.preventDefault()

            if (walletType && walletType.includes('Solana')) {
                connectWallet(ChainId.Solana)
                return
            } else if (walletType && walletType.includes('Tron')) {
                connectWallet(ChainId.Tron)
                return
            } else if (walletType && walletType.includes('EVM')) {
                connectWallet(ChainId.Ethereum)
                return
            }
            return // Early return for any wallet connection
        }

        // Handle MetaMask popups for various operations
        if (currentStep === StepNames.DEPOSIT_WETH) {
            onMetaMaskPopup && onMetaMaskPopup(StepNames.DEPOSIT_WETH_PENDING)
        } else if (currentStep === StepNames.APPROVE_TOKENS) {
            onMetaMaskPopup && onMetaMaskPopup(StepNames.APPROVE_TOKENS_PENDING)
        } else if (currentStep === StepNames.CONFIRM_SWAP) {
            onMetaMaskPopup && onMetaMaskPopup(StepNames.CONFIRM_SWAP_PENDING)
        }

        // We don't call props.onClick here anymore since we're using combinedOnClick
    }

    // Extract onClick from props to prevent it from overriding our handler
    const { onClick: propsOnClick, ...otherProps } = props

    // Create a combined onClick handler
    const combinedOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        // First run our internal handler
        handleButtonClick(event)

        // If event wasn't prevented and we have a props onClick, call it
        if (!event.defaultPrevented && propsOnClick) {
            propsOnClick(event)
        }
    }

    return (
        <button
            className={buttonClass}
            disabled={finalButtonDisabled && !buttonTextValue.includes('Connect')}
            onClick={combinedOnClick}
            {...otherProps}
        >
            <div className='flex items-center justify-center gap-2'>
                {isPendingState && <Loader2 className='h-5 w-5 animate-spin' />}
                {buttonTextValue.includes('Connect') && (
                    <>
                        {buttonTextValue.includes('EVM') && (
                            <img src={'assets/network/ethereum.png'} alt='evm' className='h-6 w-6' />
                        )}
                        {buttonTextValue.includes('Solana') && (
                            <img src={'assets/network/solana.png'} alt='solana' className='h-6 w-6' />
                        )}
                        {buttonTextValue.includes('Tron') && (
                            <img src={'assets/network/tron.png'} alt='tron' className='h-6 w-6' />
                        )}
                    </>
                )}
                {buttonText === 'Swap' ? buttonTextValue : buttonText}
            </div>
        </button>
    )
}
